import { useQuery } from "@tanstack/react-query";
import { executeQuery, getFilePath } from "../../../utils";
import { useParams } from "react-router-dom";
import { EmptyLearnerPage } from "../Layouts/EmptyLearnerPage";
import { Loading } from "../Loading";
import UnitDisplay from "../UnitDisplay";
import { CourseGroup, CoursesResponse } from "../../types";
import clsx from "clsx";

type SyllabusResponse = {
  courseName: string;
  courseDescription: string;
  courseIconUrl: string;
  units: Array<{
    unitName: string;
    unitDescription: string;
    subunits: Array<{
      subunitName: string;
      skills: string[];
    }>;
  }>;
};

const Syllabus = () => {
  const { courseId } = useParams();

  const { data: groupsData } = useQuery<{
    groups: CourseGroup[];
  }>({
    queryKey: ["groupsQuery"],
    queryFn: () =>
      executeQuery({
        path: "/learner/default/courses/groups",
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const { data: courseData } = useQuery<SyllabusResponse>({
    queryKey: ["courseSyllabusQuery", courseId],
    queryFn: () =>
      executeQuery({
        path: `/learner/default/syllabus/${courseId}`,
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const { data: coursesData } = useQuery<CoursesResponse>({
    queryKey: ["courseQuery"],
    queryFn: () =>
      executeQuery({
        path: "/learner/default/courses",
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const courseWithGroupData = coursesData?.find(
    (c) => c.course.friendlyPath === courseId || c.course.id === courseId
  );
  const group = groupsData?.groups.find(
    (g) => g._id === courseWithGroupData?.course.groupId
  );

  return (
    <EmptyLearnerPage title="Syllabus" dontTrackTime>
      <div className="p-6">
        <div className="mb-10">
          <img
            src={getFilePath("/images/DeltaMath-Logo_Home.svg")}
            alt="DeltaMath for Home"
          />
        </div>
        {!courseData ? (
          <Loading />
        ) : (
          <>
            <div className="mb-10 flex items-center gap-10 border-b pb-10">
              <div
                className={clsx(
                  "relative flex shrink-0 items-center justify-center"
                )}
              >
                <img
                  src={courseData.courseIconUrl}
                  alt={courseData.courseName}
                  className="h-32 w-32"
                />
                {group?.name?.includes("Prep") && (
                  <span
                    className={clsx(
                      "absolute flex h-[13px] items-center rounded-full bg-[#F0A72E] px-1 text-[10px] uppercase",
                      "right-[26px] top-[26px]"
                    )}
                  >
                    Prep
                  </span>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <h2 className="font-serif text-2xl font-bold text-dm-charcoal-800">
                  {courseData.courseName}
                </h2>
                <p className="font-sans text-sm !leading-none text-dm-charcoal-500">
                  {courseData.units.length}{" "}
                  {courseData.units.length === 1 ? "Unit" : "Units"}
                </p>
                <p className="font-sans text-dm-charcoal-500">
                  {courseData.courseDescription}
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-4">
              {courseData.units.map((u, i) => (
                <UnitDisplay key={i} unit={u} unitNumber={i + 1} />
              ))}
            </div>
          </>
        )}
      </div>
    </EmptyLearnerPage>
  );
};

export default Syllabus;
